import { Decimal } from "decimal.js";
import { type ReactNode, useEffect, useState } from "react";

import { VStack } from "../Stack/VStack.tsx";
import { TextField, type TextFieldProps } from "../TextField/TextField.tsx";

export function DecimalField(
  props: TextFieldProps & {
    onValueChange(value: number | null, decimal: Decimal | null): void;
    onInternalErrorChange?: (error: boolean) => void;
  },
): ReactNode {
  const [textValue, setTextValue] = useState<string>(props.value ?? "");
  const [decimalValue, setDecimalValue] = useState<Decimal | null>(
    props.value != null ? new Decimal(props.value) : null,
  );
  const [decimalError, setDecimalError] = useState<Error | null>(null);

  useEffect(() => {
    setTextValue(props.value ?? "");
  }, [props.value]);

  useEffect(() => {
    setDecimalError(null);
    try {
      const value = textValue.trim() !== "" ? new Decimal(textValue) : null;

      if (value != null) {
        const parsedDecimal = new Decimal(value.toNumber());

        if (Number.isNaN(parsedDecimal.toNumber())) {
          throw new DecimalFieldError("Invalid Number");
        }

        if (!Number.isFinite(parsedDecimal.toNumber())) {
          throw new DecimalFieldError("Invalid Number: Infinity");
        }

        if (!value.equals(parsedDecimal)) {
          // Overflow
          throw new DecimalFieldError("Too many digits");
        }
      }

      setDecimalValue(value);
    } catch (error) {
      // we don't report internal errors of decimal.js
      setDecimalError(error instanceof DecimalFieldError ? error : new Error("Invalid number"));
      setDecimalValue(null);
    }
  }, [textValue]);

  // make the outside aware of the current error state
  useEffect(() => {
    if (props.onInternalErrorChange) {
      props.onInternalErrorChange(!!decimalError);
    }
  }, [decimalError]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: don't react to props.onValueChange
  useEffect(() => {
    props.onValueChange(decimalValue?.toNumber() ?? null, decimalValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decimalValue]);

  return (
    <VStack>
      <TextField
        {...props}
        value={textValue}
        onChange={(value) => {
          props.onChange?.(value);
          setTextValue(value.replace(/,/g, "")); // `,` are not allowed, but we replace it just in case of pasting en-US formatted value
        }}
        errorMessage={
          (props.errorMessage ?? "") +
          (props.errorMessage && decimalError?.message ? ", " : "") +
          (decimalError?.message ?? "")
        }
        isInvalid={props.isInvalid || !!decimalError}
        onKeyDown={(e) => {
          props.onKeyDown?.(e);

          const isCtrlKey = e.ctrlKey || e.metaKey;
          if (isCtrlKey && (e.key === "c" || e.key === "v" || e.key === "x" || e.key === "a")) {
            return; // Allow Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A
          }

          const allowedKeys = [
            "Backspace",
            "Delete",
            "ArrowLeft",
            "ArrowRight",
            "Tab",
            "0",
            "1",
            "2",
            "3",
            "4",
            "5",
            "6",
            "7",
            "8",
            "9",
            "-",
            ".",
            "e",
            "+",
          ];

          if (!allowedKeys.includes(e.key)) {
            e.preventDefault();
          }

          const value = (e.currentTarget as HTMLInputElement).value;

          // Prevent multiple occurrences of 'e', '.'
          if ((e.key === "e" && value.includes("e")) || (e.key === "." && value.includes("."))) {
            e.preventDefault();
          }

          // Prevent '-' and '+' unless they are at the start or immediately after 'e' or 'E'
          const cursorPos = e.currentTarget.selectionStart;
          if ((e.key === "-" || e.key === "+") && cursorPos !== 0 && value[cursorPos - 1] !== "e") {
            e.preventDefault();
          }
        }}
      />
    </VStack>
  );
}

class DecimalFieldError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MyCustomError";
    Error.captureStackTrace(this, DecimalFieldError);
  }
}
